import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Avatar,
  Collapse,
  Stack,
  Chip,
  IconButton,
} from '@mui/material';
import {
  People as PeopleIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { COLORS, SECTION_STYLES } from './MAMCStyles';

// Styled table components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: COLORS.text.primary,
  borderBottom: `1px solid ${COLORS.background.light}`,
  padding: theme.spacing(1.5),
  '&.header': {
    color: COLORS.text.accent,
    fontWeight: 'bold',
    borderBottom: `2px solid ${COLORS.secondary}`,
  },
}));

const RegisteredTeams = ({ teams }) => {
  const [expandedTeam, setExpandedTeam] = useState(null);

  // Filter to show only teams that have paid registration fee, sorted alphabetically by team name
  const paidTeams = teams
    .filter(team => team.registration_fee_paid)
    .sort((a, b) => a.team_name.localeCompare(b.team_name, undefined, { sensitivity: 'base' }));

  // Return null if no paid teams
  if (paidTeams.length === 0) {
    return null;
  }

  const toggleExpand = teamId => {
    setExpandedTeam(expandedTeam === teamId ? null : teamId);
  };

  return (
    <Box
      sx={{
        ...SECTION_STYLES.groupStandings,
        p: { xs: 2, sm: 3 },
        mb: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          pb: 2,
          borderBottom: `2px solid ${COLORS.background.light}`,
        }}
      >
        <PeopleIcon
          sx={{
            color: COLORS.secondary,
            fontSize: 28,
            filter: 'drop-shadow(0 2px 4px rgba(218,165,32,0.3))',
          }}
        />
        <Typography
          variant="h6"
          sx={{
            color: COLORS.text.accent,
            fontWeight: 'bold',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
            letterSpacing: '1px',
          }}
        >
          Registered Teams ({paidTeams.length})
        </Typography>
      </Box>

      {/* Teams Table */}
      <Box sx={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
        <Table sx={{ minWidth: { xs: 400, sm: 600 } }}>
          <TableHead>
            <TableRow>
              <StyledTableCell className="header">#</StyledTableCell>
              <StyledTableCell className="header">Team Name</StyledTableCell>
              <StyledTableCell className="header" align="center"></StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paidTeams.map((team, index) => (
              <React.Fragment key={team.id}>
                {/* Main Team Row */}
                <TableRow
                  sx={{
                    cursor: 'pointer',
                    backgroundColor:
                      expandedTeam === team.id ? 'rgba(218,165,32,0.1)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(218,165,32,0.15)',
                    },
                    transition: 'background-color 0.2s ease',
                  }}
                  onClick={() => toggleExpand(team.id)}
                >
                  <StyledTableCell>{index + 1}</StyledTableCell>
                  <StyledTableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        sx={{
                          width: { xs: 24, sm: 28 },
                          height: { xs: 24, sm: 28 },
                          bgcolor: COLORS.secondary,
                          fontSize: { xs: '0.8rem', sm: '0.9rem' },
                          fontWeight: 'bold',
                        }}
                      >
                        {team.team_name.charAt(0)}
                      </Avatar>
                      <Typography
                        sx={{
                          color: COLORS.text.primary,
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          fontWeight: 'medium',
                        }}
                      >
                        {team.team_name}
                      </Typography>
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <IconButton
                      size="small"
                      sx={{
                        color: COLORS.text.accent,
                        '&:hover': {
                          color: COLORS.secondary,
                        },
                      }}
                    >
                      {expandedTeam === team.id ? (
                        <KeyboardArrowUpIcon />
                      ) : (
                        <KeyboardArrowDownIcon />
                      )}
                    </IconButton>
                  </StyledTableCell>
                </TableRow>

                {/* Expandable Details Row */}
                <TableRow>
                  <StyledTableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
                    <Collapse in={expandedTeam === team.id} timeout="auto" unmountOnExit>
                      <Box
                        sx={{ margin: 2, p: 2, bgcolor: 'rgba(0, 0, 0, 0.03)', borderRadius: 1 }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 2,
                            color: COLORS.text.accent,
                            fontWeight: 'bold',
                          }}
                        >
                          Team Details
                        </Typography>

                        {/* Players List */}
                        {team.players && team.players.length > 0 ? (
                          <Stack spacing={1} sx={{ mb: 2 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: COLORS.text.secondary,
                                fontWeight: 'medium',
                                mb: 1,
                              }}
                            >
                              Players (
                              {
                                team.players.filter(
                                  player => player.player_name.toLowerCase() !== 'own goal'
                                ).length
                              }
                              ):
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {team.players
                                .filter(player => player.player_name.toLowerCase() !== 'own goal')
                                .map((player, playerIndex) => (
                                  <Chip
                                    key={playerIndex}
                                    label={`${playerIndex + 1}. ${player.player_name}`}
                                    size="small"
                                    sx={{
                                      bgcolor: 'rgba(218, 165, 32, 0.1)',
                                      color: COLORS.text.primary,
                                      fontSize: '0.75rem',
                                    }}
                                  />
                                ))}
                            </Box>
                          </Stack>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              color: COLORS.text.secondary,
                              fontStyle: 'italic',
                              mb: 2,
                            }}
                          >
                            No players registered yet
                          </Typography>
                        )}

                        {/* Manager */}
                        {team.manager_name && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ fontSize: 16, color: COLORS.secondary }} />
                            <Typography
                              variant="body2"
                              sx={{
                                color: COLORS.text.secondary,
                                fontWeight: 'medium',
                              }}
                            >
                              Manager: {team.manager_name}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </StyledTableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default RegisteredTeams;
